import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Minus, Pencil, Plus, Trash2, UserPlus } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ALL_PERMISSIONS, PERMISSIONS, roleInput, type Permission } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, errorMessage } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useBarbers, useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { useRoles, type Invitation, type Member, type Role } from '../catalog/api';
import { applyServerError, emptyToNull, zodForm } from '../catalog/lib';
import { InviteModal } from './invite-modal';
import { Section } from './section';

const SECTIONS = ['membros', 'convites', 'papeis'] as const;

export function MembersPage() {
  const [tab, setTab] = useQueryState('secao', parseAsStringLiteral(SECTIONS).withDefault('membros'));
  return (
    <Section title="Equipe e permissões" description="Quem acessa o sistema, o que cada papel pode fazer e os convites pendentes.">
      <Tabs value={tab} onValueChange={(v) => setTab(v as (typeof SECTIONS)[number])}>
        <TabsList>
          <TabsTrigger value="membros">Membros</TabsTrigger>
          <TabsTrigger value="convites">Convites</TabsTrigger>
          <TabsTrigger value="papeis">Papéis e permissões</TabsTrigger>
        </TabsList>
        <TabsContent value="membros" className="pt-4">
          <MembersTab />
        </TabsContent>
        <TabsContent value="convites" className="pt-4">
          <InvitationsTab />
        </TabsContent>
        <TabsContent value="papeis" className="pt-4">
          <RolesTab />
        </TabsContent>
      </Tabs>
    </Section>
  );
}

// ---------- membros ----------
function MembersTab() {
  const qc = useQueryClient();
  const { me } = useSession();
  const members = useQuery({ queryKey: ['org', 'members'], queryFn: () => api.get<Member[]>('/org/members') });
  const roles = useRoles();
  const barbers = useBarbers();
  const [invite, setInvite] = useState(false);

  const update = async (m: Member, body: { roleId?: string; barberId?: string | null; active?: boolean }, ok: string) => {
    try {
      await api.patch(`/org/members/${m.id}`, body);
      toast.success(ok);
      qc.invalidateQueries({ queryKey: ['org', 'members'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (members.isLoading) return <LoadingState />;
  if (members.error) return <ErrorState error={members.error} onRetry={() => members.refetch()} />;

  return (
    <div className="grid gap-4">
      <div>
        <Button onClick={() => setInvite(true)}>
          <UserPlus aria-hidden /> Enviar convite
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Nome</TableHead>
              <TableHead>Papel</TableHead>
              <TableHead>Profissional vinculado</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Ações</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.data?.map((m) => {
              const self = m.userId === me?.user.id;
              return (
                <TableRow key={m.id} className={m.active ? undefined : 'opacity-60'}>
                  <TableCell>
                    <p className="font-medium">
                      {m.name} {self && <span className="text-xs font-normal text-muted-foreground">(você)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </TableCell>
                  <TableCell>
                    <Select value={m.roleId} disabled={self} onValueChange={(v) => update(m, { roleId: v }, 'Papel alterado')}>
                      <SelectTrigger className="w-40" aria-label={`Papel de ${m.name}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.data?.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={m.barberId ?? 'none'} onValueChange={(v) => update(m, { barberId: v === 'none' ? null : v }, 'Vínculo salvo')}>
                      <SelectTrigger className="w-44" aria-label={`Profissional vinculado a ${m.name}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {barbers.data?.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.active ? 'secondary' : 'outline'}>{m.active ? 'Ativo' : 'Desativado'}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {self ? null : m.active ? (
                      <ConfirmDialog
                        trigger={<Button variant="outline" size="sm">Desativar</Button>}
                        title={`Desativar ${m.name}?`}
                        description="A pessoa perde o acesso ao sistema agora. O histórico dela é mantido e você pode reativar depois."
                        confirmLabel="Desativar acesso"
                        onConfirm={() => update(m, { active: false }, 'Acesso desativado')}
                      />
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => update(m, { active: true }, 'Acesso reativado')}>
                        Reativar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <InviteModal open={invite} onOpenChange={setInvite} />
    </div>
  );
}

// ---------- convites ----------
function inviteStatus(i: Invitation) {
  if (i.acceptedAt) return { label: 'Aceito', variant: 'secondary' as const, pending: false };
  if (i.revokedAt) return { label: 'Revogado', variant: 'outline' as const, pending: false };
  if (new Date(i.expiresAt) < new Date()) return { label: 'Expirado', variant: 'outline' as const, pending: false };
  return { label: 'Pendente', variant: 'default' as const, pending: true };
}

function InvitationsTab() {
  const qc = useQueryClient();
  const tz = useTimezone();
  const list = useQuery({ queryKey: ['org', 'invitations'], queryFn: () => api.get<Invitation[]>('/org/invitations') });
  const [invite, setInvite] = useState(false);

  if (list.isLoading) return <LoadingState />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => list.refetch()} />;

  return (
    <div className="grid gap-4">
      <div>
        <Button onClick={() => setInvite(true)}>
          <UserPlus aria-hidden /> Enviar convite
        </Button>
      </div>
      {list.data?.length === 0 ? (
        <EmptyState title="Nenhum convite enviado" description="Convide a equipe por e-mail para que cada pessoa tenha o próprio acesso." />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>E-mail</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Validade</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.map((i) => {
                const s = inviteStatus(i);
                return (
                  <TableRow key={i.id}>
                    <TableCell className="font-medium">{i.email}</TableCell>
                    <TableCell>{i.roleName}</TableCell>
                    <TableCell>
                      <Badge variant={s.variant}>{s.label}</Badge>
                    </TableCell>
                    <TableCell className="tabular">{fmtDate(i.expiresAt, tz)}</TableCell>
                    <TableCell className="text-right">
                      {s.pending && (
                        <ConfirmDialog
                          trigger={<Button variant="outline" size="sm">Revogar</Button>}
                          title={`Revogar o convite de ${i.email}?`}
                          description="O link enviado deixa de funcionar. Você pode enviar um novo convite depois."
                          confirmLabel="Revogar convite"
                          onConfirm={async () => {
                            try {
                              await api.del(`/org/invitations/${i.id}`);
                              toast.success('Convite revogado');
                              qc.invalidateQueries({ queryKey: ['org', 'invitations'] });
                            } catch (e) {
                              toast.error(errorMessage(e));
                            }
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <InviteModal open={invite} onOpenChange={setInvite} />
    </div>
  );
}

// ---------- papéis ----------
export const PERMISSION_GROUPS: { label: string; keys: Permission[] }[] = [
  { label: 'Agenda', keys: ['agenda.read', 'agenda.read_all', 'agenda.write', 'agenda.block'] },
  { label: 'Clientes', keys: ['customers.read', 'customers.write', 'customers.export', 'loyalty.manage', 'campaigns.manage'] },
  { label: 'Caixa', keys: ['cash.operate', 'cash.manage', 'expenses.manage'] },
  { label: 'Comissões', keys: ['commissions.read', 'commissions.read_all', 'commissions.manage'] },
  { label: 'Relatórios', keys: ['reports.read'] },
  { label: 'Configurações', keys: ['catalog.manage', 'settings.manage', 'members.manage', 'audit.read'] },
  { label: 'Site', keys: ['site.edit', 'site.publish'] },
  { label: 'Assistente de IA', keys: ['ai.use'] },
];
// qualquer permissão nova que ainda não tenha grupo aparece em "Outras"
const grouped = new Set(PERMISSION_GROUPS.flatMap((g) => g.keys));
const leftovers = ALL_PERMISSIONS.filter((p) => !grouped.has(p));
const GROUPS = leftovers.length ? [...PERMISSION_GROUPS, { label: 'Outras', keys: leftovers }] : PERMISSION_GROUPS;

function RolesTab() {
  const qc = useQueryClient();
  const roles = useRoles();
  const [editing, setEditing] = useState<Role | null>(null);
  const [open, setOpen] = useState(false);

  if (roles.isLoading) return <LoadingState />;
  if (roles.error) return <ErrorState error={roles.error} onRetry={() => roles.refetch()} />;
  const list = roles.data ?? [];

  const remove = async (r: Role) => {
    try {
      await api.del(`/org/roles/${r.id}`);
      toast.success('Papel excluído');
      qc.invalidateQueries({ queryKey: ['org', 'roles'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="grid gap-4">
      <div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus aria-hidden /> Novo papel
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-[40rem] text-sm">
          <caption className="sr-only">Permissões de cada papel</caption>
          <thead>
            <tr className="border-b">
              <th scope="col" className="sticky left-0 bg-card px-4 py-3 text-left font-medium text-muted-foreground">
                Permissão
              </th>
              {list.map((r) => (
                <th key={r.id} scope="col" className="min-w-28 px-3 py-3 text-center align-bottom">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-semibold">{r.name}</span>
                    {r.key === 'owner' ? (
                      <Badge variant="outline" className="gap-1">
                        <Lock className="size-3" aria-hidden /> Somente leitura
                      </Badge>
                    ) : (
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          aria-label={`Editar papel ${r.name}`}
                          onClick={() => {
                            setEditing(r);
                            setOpen(true);
                          }}
                        >
                          <Pencil aria-hidden />
                        </Button>
                        {!r.key && (
                          <ConfirmDialog
                            trigger={
                              <Button variant="ghost" size="icon" className="size-7" aria-label={`Excluir papel ${r.name}`}>
                                <Trash2 className="text-destructive" aria-hidden />
                              </Button>
                            }
                            title={`Excluir o papel "${r.name}"?`}
                            description="Só é possível excluir papéis sem membros. Quem tem este papel precisa ser movido antes."
                            confirmLabel="Excluir papel"
                            onConfirm={() => remove(r)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {GROUPS.map((g) => (
            <tbody key={g.label} className="border-b last:border-0">
              <tr className="bg-muted/50">
                <th colSpan={list.length + 1} scope="colgroup" className="px-4 py-1.5 text-left text-xs font-semibold text-muted-foreground">
                  {g.label}
                </th>
              </tr>
              {g.keys.map((p) => (
                <tr key={p} className="border-t">
                  <th scope="row" className="sticky left-0 bg-card px-4 py-2 text-left font-normal">
                    {PERMISSIONS[p]}
                  </th>
                  {list.map((r) => {
                    const has = r.key === 'owner' || r.permissions.includes(p);
                    return (
                      <td key={r.id} className="px-3 py-2 text-center">
                        {has ? <Check className="mx-auto size-4 text-primary" aria-label="Permitido" /> : <Minus className="mx-auto size-4 text-muted-foreground/50" aria-label="Não permitido" />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
      <RoleModal open={open} onOpenChange={setOpen} role={editing} />
    </div>
  );
}

interface RoleValues {
  name: string;
  description: string;
  permissions: string[];
}

function RoleModal({ open, onOpenChange, role }: { open: boolean; onOpenChange: (v: boolean) => void; role: Role | null }) {
  const qc = useQueryClient();
  const form = useForm<RoleValues>({
    resolver: zodForm<RoleValues>(roleInput, (v) => ({ ...v, description: emptyToNull(v.description) })),
    defaultValues: { name: '', description: '', permissions: [] },
  });
  const { errors } = form.formState;
  const perms = form.watch('permissions');

  useEffect(() => {
    if (open) form.reset({ name: role?.name ?? '', description: role?.description ?? '', permissions: role?.permissions ?? [] });
  }, [open, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: RoleValues) => {
      const body = roleInput.parse({ ...v, description: emptyToNull(v.description) });
      return role ? api.put(`/org/roles/${role.id}`, body) : api.post('/org/roles', body);
    },
    onSuccess: () => {
      toast.success('Papel salvo');
      qc.invalidateQueries({ queryKey: ['org', 'roles'] });
      qc.invalidateQueries({ queryKey: ['org', 'members'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e),
  });

  const toggle = (keys: string[], on: boolean) => {
    const set = new Set(perms);
    keys.forEach((k) => (on ? set.add(k) : set.delete(k)));
    form.setValue('permissions', [...set], { shouldDirty: true });
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={role ? `Editar papel ${role.name}` : 'Novo papel'}
      description={role?.key ? 'Papel padrão do sistema: você pode ajustar as permissões, mas não excluí-lo.' : 'Escolha o que quem tiver este papel pode fazer.'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="role-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar papel'}
          </Button>
        </>
      }
    >
      <form id="role-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-5" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="role-name" label="Nome do papel" error={errors.name?.message}>
            <Input id="role-name" autoFocus aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field id="role-desc" label="Descrição (opcional)" error={errors.description?.message}>
            <Input id="role-desc" {...form.register('description')} />
          </Field>
        </div>
        {errors.permissions?.message && (
          <p role="alert" className="text-xs text-destructive">
            {errors.permissions.message as string}
          </p>
        )}
        <div className="grid gap-4">
          {GROUPS.map((g) => {
            const all = g.keys.every((k) => perms.includes(k));
            return (
              <fieldset key={g.label} className="rounded-md border p-3">
                <legend className="px-1 text-sm font-semibold">{g.label}</legend>
                <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={all} onCheckedChange={(v) => toggle(g.keys, v === true)} aria-label={`Marcar todas as permissões de ${g.label}`} />
                  Marcar todas
                </label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {g.keys.map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={perms.includes(k)} onCheckedChange={(v) => toggle([k], v === true)} />
                      {PERMISSIONS[k]}
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
      </form>
    </FormModal>
  );
}
